import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Mail, Calendar, MessageCircle } from 'react-feather';
import { Helmet } from 'react-helmet-async';
import { useDarkMode } from './DarkModeContext';
import { trackEvent } from './utils/amplitude';
import Footer from './Footer';

function Contact() {
    const { darkMode } = useDarkMode();
    
    const handleCalendlyClick = () => {
        trackEvent('button_clicked', { buttonName: 'Book a Call', page: 'Contact' });
        window.open('https://calendly.com/boukar-o-sall/30min', '_blank');
    };

    const handleLinkedInClick = () => {
        trackEvent('button_clicked', { buttonName: 'LinkedIn Contact', page: 'Contact' });
        window.open('https://www.linkedin.com/in/boukar-sall', '_blank');
    };

    const handleEmailClick = () => {
        trackEvent('button_clicked', { buttonName: 'Email Contact', page: 'Contact' });
        window.location.href = 'mailto:hello@merra.ai';
    };
    
    return (
        <>
        <Helmet>
            <title>Contact Us - Merra.ai</title>
            <meta name="description" content="Get in touch with Merra. Book a call, send us an email, or connect on LinkedIn. We'd love to hear from you and discuss how Merra can help your business." />
            <meta name="keywords" content="contact Merra, Merra support, Merra contact, get in touch, Merra help" />
            <link rel="canonical" href="https://www.merra.ai/contact" />
            <meta property="og:title" content="Contact Us - Merra.ai" />
            <meta property="og:description" content="Get in touch with Merra. Book a call, send us an email, or connect on LinkedIn." />
            <meta property="og:url" content="https://www.merra.ai/contact" />
        </Helmet>
        <div style={{ 
            maxWidth: '800px', 
            margin: '0 auto', 
            padding: '20px',
            fontFamily: 'Arial, sans-serif',
            lineHeight: '1.6',
            color: darkMode ? '#e0e0e0' : '#333',
            backgroundColor: darkMode ? '#1a1a1a' : 'white',
            minHeight: '100vh'
        }}>
            <div style={{ marginBottom: '30px' }}>
                <Link to="/" style={{ color: darkMode ? '#4A9EFF' : '#1976d2', textDecoration: 'none' }}>
                    ← Back to Home
                </Link>
            </div>

            <h1 style={{ 
                color: darkMode ? '#ffffff' : '#2c3e50', 
                borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, 
                paddingBottom: '10px',
                textAlign: 'center',
                marginBottom: '40px'
            }}>
                Contact Us
            </h1>

            <div style={{ 
                textAlign: 'center', 
                marginBottom: '50px',
                fontSize: '18px',
                color: darkMode ? '#e0e0e0' : '#666'
            }}>
                We'd love to hear from you. Get in touch with us through any of the channels below.
            </div>

            {/* Contact Options */}
            <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '30px',
                marginBottom: '50px'
            }}>
                {/* Book a Call */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                    border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    padding: '30px',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer'
                }}
                onClick={handleCalendlyClick}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = darkMode ? '0 8px 16px rgba(74, 158, 255, 0.2)' : '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
                >
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: '20px'
                    }}>
                        <Calendar 
                            size={48} 
                            color={darkMode ? '#AB5898' : '#AB5898'}
                        />
                    </div>
                    <h2 style={{ 
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: 0,
                        marginBottom: '15px',
                        fontSize: '24px'
                    }}>
                        Book a Call
                    </h2>
                    <p style={{ 
                        color: darkMode ? '#e0e0e0' : '#666',
                        marginBottom: '20px',
                        fontSize: '16px'
                    }}>
                        Schedule a 30-minute call with our founder to discuss how Merra can help your business.
                    </p>
                    <div style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: darkMode ? '#4A9EFF' : '#1976d2',
                        fontWeight: 'bold',
                        fontSize: '16px'
                    }}>
                        Schedule on Calendly
                        <ExternalLink size={18} />
                    </div>
                </div>

                {/* Email */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                    border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    padding: '30px',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer'
                }}
                onClick={handleEmailClick}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = darkMode ? '0 8px 16px rgba(74, 158, 255, 0.2)' : '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
                >
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: '20px'
                    }}>
                        <Mail 
                            size={48} 
                            color={darkMode ? '#AB5898' : '#AB5898'}
                        />
                    </div>
                    <h2 style={{ 
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: 0,
                        marginBottom: '15px',
                        fontSize: '24px'
                    }}>
                        Send us an Email
                    </h2>
                    <p style={{ 
                        color: darkMode ? '#e0e0e0' : '#666',
                        marginBottom: '20px',
                        fontSize: '16px'
                    }}>
                        Drop us a line at hello@merra.ai. We read every email and will get back to you as soon as possible.
                    </p>
                    <div style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: darkMode ? '#4A9EFF' : '#1976d2',
                        fontWeight: 'bold',
                        fontSize: '16px'
                    }}>
                        hello@merra.ai
                        <Mail size={18} />
                    </div>
                </div>

                {/* LinkedIn */}
                <div style={{ 
                    backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                    border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`,
                    borderRadius: '12px',
                    padding: '30px',
                    textAlign: 'center',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer'
                }}
                onClick={handleLinkedInClick}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = darkMode ? '0 8px 16px rgba(74, 158, 255, 0.2)' : '0 8px 16px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
                >
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        marginBottom: '20px'
                    }}>
                        <MessageCircle 
                            size={48} 
                            color={darkMode ? '#AB5898' : '#AB5898'}
                        />
                    </div>
                    <h2 style={{ 
                        color: darkMode ? '#ffffff' : '#2c3e50',
                        marginTop: 0,
                        marginBottom: '15px',
                        fontSize: '24px'
                    }}>
                        Connect on LinkedIn
                    </h2>
                    <p style={{ 
                        color: darkMode ? '#e0e0e0' : '#666',
                        marginBottom: '20px',
                        fontSize: '16px'
                    }}>
                        Send us a message on LinkedIn. We're always happy to chat about Merra and how it can help you.
                    </p>
                    <div style={{ 
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: darkMode ? '#4A9EFF' : '#1976d2',
                        fontWeight: 'bold',
                        fontSize: '16px'
                    }}>
                        Message on LinkedIn
                        <ExternalLink size={18} />
                    </div>
                </div>
            </div>

            {/* Additional Information */}
            <div style={{ 
                marginTop: '50px', 
                padding: '30px',
                backgroundColor: darkMode ? '#2a2a2a' : '#f8f9fa',
                borderRadius: '8px',
                border: `1px solid ${darkMode ? '#444' : '#e0e0e0'}`
            }}>
                <h2 style={{ 
                    color: darkMode ? '#ffffff' : '#2c3e50', 
                    marginTop: 0,
                    marginBottom: '20px',
                    borderBottom: `2px solid ${darkMode ? '#AB5898' : '#AB5898'}`, 
                    paddingBottom: '10px' 
                }}>
                    Frequently Asked Questions
                </h2>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        How quickly do you respond?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        We typically respond within 24-48 hours. For urgent matters, booking a call is the fastest way to reach us.
                    </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        Can I schedule a demo?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        Yes! Book a call with our founder to see Merra in action and discuss how it can help your business.
                    </p>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ color: darkMode ? '#e0e0e0' : '#34495e', marginBottom: '10px' }}>
                        Do you offer support?
                    </h3>
                    <p style={{ color: darkMode ? '#e0e0e0' : '#333', margin: 0 }}>
                        Yes, we provide support to all our users. Reach out through LinkedIn or book a call if you need assistance.
                    </p>
                </div>
            </div>
            <Footer />
        </div>
        </>
    );
}

export default Contact;

